import * as yup from "yup";
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import React, { useMemo } from "react";

yup.addMethod(yup.array, 'emails', function format({ reg, msg }) {
  return this.test(function (value) {
    const invalidEmails = value.filter(elem => !new RegExp(reg).test(elem))
    return !invalidEmails.length
      ? true
      : this.createError({
        message: <FormattedMessage id="invitepeoplemodal.generic.error" />
      });
  });
});

yup.addMethod(yup.array, 'email', function format({ reg, msg, required }) {
  return yup.array().of(
    yup.object().shape({
      email: yup.string()
        .email(<FormattedMessage id="inviteauthorisedmodal.email.error"/>)
    }))
});

yup.addMethod(yup.array, 'eachItemMatch', function ({ reg, msg }) {
  return this.test('eachItemMatch', msg, function (value) {
    const { path, createError } = this;

    // if the field is missing or empty, don't validate
    if (!value || value.length === 0) {
      return true;
    }

    const regex = new RegExp(reg);
    const nonConformingItems = value.filter(item => !regex.test(item));

    if (nonConformingItems.length === 0) {
      return true;
    }

    return createError({
      path,
      message: `${msg} ${nonConformingItems.join(', ')}`,
    });
  });
});

function isNotNull(value) {
  return !(value === null || value === undefined);
}

function isConditionMeet(value, is) {
  value = value.toString();

  if (typeof is === 'function') return is(value);
  if (Array.isArray(is)) return is.find(v => value === v);

  is = is.toString();

  return value === is;
}

function _createValidationPolicyYupSchema({type, fieldName, validations: {validators = {}, msgs = {}} = {}} = {}) {
  let fieldYupValidations;
  if (type) {
    fieldYupValidations = yup[type]();
  } else {
    fieldYupValidations = yup.string();
  }

  for (const [validationType, validationRule] of Object.entries(validators)) {
    //this is used to eliminate the null validations that are coming from the BE
    if (isNotNull(validationRule)) {
      let validationMessage = msgs?.[validationType]?.replace("{0}", fieldName);

      switch (validationType) {
      case 'emails':
        validationMessage = validationMessage?.replace(fieldName, "${custom}"); //todo: refactor
        fieldYupValidations = fieldYupValidations[validationType]({
          reg: validators[validationType],
          msg: validationMessage,
        });
        break;
      case 'required':
         if (validationRule) {
          fieldYupValidations = fieldYupValidations.required(validationMessage);

          // min one item if array and required
          if (type === 'array') {
            fieldYupValidations = fieldYupValidations.min(1, validationMessage);
          }
        }
        break;
      case 'when':
        const [relatedField, conditionalValidation] = validationRule;
        fieldYupValidations = fieldYupValidations[validationType](relatedField, (value, schema) => {
          const conditionResult = isConditionMeet(value, conditionalValidation.is) ? 'then' : 'otherwise';

          return _createValidationPolicyYupSchema({
            type: schema.type,
            fieldName,
            ...conditionalValidation[conditionResult]
          });
        });
        break;
      case 'matches':
        fieldYupValidations = fieldYupValidations[validationType](validationRule, {message: validationMessage});
        break;
      case 'transform':
        fieldYupValidations = fieldYupValidations[validationType]((value) => typeof validationRule === "function" ? validationRule(value) : validationRule);
        break;
      case 'eachItemMatch':
        fieldYupValidations = fieldYupValidations[validationType]({
          reg: validationRule,
          msg: validationMessage,
        });
        break;
      default:
        // the rest of validations need a param being set as the validationRule
        fieldYupValidations = fieldYupValidations[validationType](
          validationRule,
          validationMessage
        );
        break;
      }
    }
  }

  return fieldYupValidations;
}

export function createValidationPolicyYupSchema(schemas) {
  let validationObject = {};

  schemas.forEach((schema) => {
    validationObject[schema.fieldName] = _createValidationPolicyYupSchema(schema)
  });
  return yup.object().shape(validationObject);
}

const validationMessages = defineMessages({
  // travelerDetailsMockedApiValidationObj
  travelerFirstNameMin: {
    id: "travelerDetailsValidation.firstNameMin",
  },
  travelerFirstNameMax: {
    id: "travelerDetailsValidation.firstNameMax",
  },
  travelerFirstNameMatches: {
    id: "travelerDetailsValidation.firstNameMatches",
  },
   travelerFirstNameUCJoinMatches: {
    id: "travelerDetailsValidation.firstNameUCJoinMatches",
  },
  travelerFirstNameRequired: {
    id: "travelerDetailsValidation.firstNameRequired",
  },
  travelerLastNameMin: {
    id: "travelerDetailsValidation.lastNameMin",
  },
  travelerLastNameMax: {
    id: "travelerDetailsValidation.lastNameMax",
  },
  travelerLastNameMatches: {
    id: "travelerDetailsValidation.lastNameMatches",
  },
   travelerLastNameUCJoinMatches: {
    id: "travelerDetailsValidation.lastNameUCJoinMatches",
  },
  travelerLastNameRequired: {
    id: "travelerDetailsValidation.lastNameRequired",
  },
  travelerEmailMin: {
    id: "travelerDetailsValidation.emailMin",
  },
  travelerEmailMax: {
    id: "travelerDetailsValidation.emailMax",
  },
  travelerEmailMatches: {
    id: "travelerDetailsValidation.emailMatches",
  },
  travelerEmailRequired: {
    id: "travelerDetailsValidation.emailRequired",
  },
  travelerRoleMin: {
    id: "travelerDetailsValidation.roleMin",
  },
  travelerRoleMax: {
    id: "travelerDetailsValidation.roleMax",
  },
  travelerRoleRequired: {
    id: "travelerDetailsValidation.roleRequired",
  },
  travelerTeamMin: {
    id: "travelerDetailsValidation.teamMin",
  },
  travelerTeamMax: {
    id: "travelerDetailsValidation.teamMax",
  },
  travelerTeamRequired: {
    id: "travelerDetailsValidation.teamRequired",
  },
  // editTeamMockedValidationObj
  editTeamNameMin: {
    id: "editTeamValidation.nameMin",
  },
  editTeamNameMax: {
    id: "editTeamValidation.nameMax",
  },
  editTeamNameMatches: {
    id: "editTeamValidation.nameMatches",
  },
  editTeamNameRequired: {
    id: "editTeamValidation.nameRequired",
  },
  // createTeamMockedValidationObj
  createTeamNameMin: {
    id: "createTeamValidation.nameMin",
  },
  createTeamNameMax: {
    id: "createTeamValidation.nameMax",
  },
  createTeamNameMatches: {
    id: "createTeamValidation.nameMatches",
  },
  createTeamNameRequired: {
    id: "createTeamValidation.nameRequired",
  },
  // companyInfoMockedApiValidationObj
  companyNameMin: {
    id: "companyInfoValidation.nameMin",
  },
  companyNameMax: {
    id: "companyInfoValidation.nameMax",
  },
  companyNameMatches: {
    id: "companyInfoValidation.nameMatches",
  },
  companyNameRequired: {
    id: "companyInfoValidation.nameRequired",
  },
  companyTaxMin: {
    id: "companyInfoValidation.taxMin",
  },
  companyTaxMax: {
    id: "companyInfoValidation.taxMax",
  },
  companyTaxMatches: {
    id: "companyInfoValidation.taxMatches",
  },
  companyTaxRequired: {
    id: "companyInfoValidation.taxRequired",
  },
  companyPhoneMin: {
    id: "companyInfoValidation.phoneMin",
  },
  companyPhoneMax: {
    id: "companyInfoValidation.phoneMax",
  },
  companyPhoneMatches: {
    id: "companyInfoValidation.phoneMatches",
  },
  companyPhoneRequired: {
    id: "companyInfoValidation.phoneRequired",
  },
  companyCountryRequired: {
    id: "companyInfoValidation.countryRequired",
  },
  companyFirstAddressMin: {
    id: "companyInfoValidation.firstAddressMin",
  },
  companyFirstAddressMax: {
    id: "companyInfoValidation.firstAddressMax",
  },
  companyFirstAddressMatches: {
    id: "companyInfoValidation.firstAddressMatches",
  },
  companyFirstAddressRequired: {
    id: "companyInfoValidation.firstAddressRequired",
  },
  companySecondAddressMin: {
    id: "companyInfoValidation.secondAddressMin",
  },
  companySecondAddressMax: {
    id: "companyInfoValidation.secondAddressMax",
  },
  companySecondAddressMatches: {
    id: "companyInfoValidation.secondAddressMatches",
  },
  companySecondAddressRequired: {
    id: "companyInfoValidation.secondAddressRequired",
  },
  companyCityMin: {
    id: "companyInfoValidation.cityMin",
  },
  companyCityMax: {
    id: "companyInfoValidation.cityMax",
  },
  companyCityMatches: {
    id: "companyInfoValidation.cityMatches",
  },
  companyCityRequired: {
    id: "companyInfoValidation.cityRequired",
  },
  companyStateRequired: {
    id: "companyInfoValidation.stateRequired",
  },
  companyPostalMin: {
    id: "companyInfoValidation.postalMin",
  },
  companyPostalMax: {
    id: "companyInfoValidation.postalMax",
  },
  companyPostalMatches: {
    id: "companyInfoValidation.postalMatches",
  },
  companyPostalRequired: {
    id: "companyInfoValidation.postalRequired",
  },
  companyPreviousAccountMatches: {
    id: "companyInfoValidation.previousAccountNumberMatch",
  },
  companyStarValidationError: {
    id: "companyInfoValidation.companyStarValidationError",
  },
  companyFieldRequired: {
    id: "companyInfoValidation.companyFieldRequired",
  },
  companyFieldMatches: {
    id: "companyInfoValidation.companyFieldMatches",
  },
  // invitePeopleMockedApiValidationObj
  inviteEmailMin: {
    id: "invitePeopleValidation.emailMin",
  },
  inviteEmailMax: {
    id: "invitePeopleValidation.emailMax",
  },
  inviteEmailMatches: {
    id: "invitePeopleValidation.emailMatches",
  },
  inviteEmailRequired: {
    id: "invitePeopleValidation.emailRequired",
  },
  inviteRoleRequired: {
    id: "invitePeopleValidation.roleRequired",
  },
  // inviteAuthorisedMockedValidationObj
  inviteAuthEmailMin: {
    id: "inviteAuthorisedValidation.emailMin",
  },
  inviteAuthEmailMax: {
    id: "inviteAuthorisedValidation.emailMax",
  },
  inviteAuthEmailMatches: {
    id: "inviteAuthorisedValidation.emailMatches",
  },
  inviteAuthEmailRequired: {
    id: "inviteAuthorisedValidation.emailRequired",
  },
  // addDomainMockedValidationObj
  addDomainMin: {
    id: "addDomainValidation.domainMin",
  },
  addDomainMax: {
    id: "addDomainValidation.domainMax",
  },
  addDomainMatches: {
    id: "addDomainValidation.domainMatches",
  },
  addDomainRequired: {
    id: "addDomainValidation.domainRequired",
  },
  addDomainEmailMin: {
    id: "addDomainValidation.emailMin",
  },
  addDomainEmailMax: {
    id: "addDomainValidation.emailMax",
  },
  addDomainEmailMatches: {
    id: "addDomainValidation.emailMatches",
  },
  addDomainEmailRequired: {
    id: "addDomainValidation.emailRequired",
  },
  // tranferMilesMockedValidationObj
  transferMilesMin: {
    id: "transferMilesValidation.milesMin",
  },
  transferMilesMatches: {
    id: "transferMilesValidation.milesMatches",
  },
  transferMilesRequired: {
    id: "transferMilesValidation.milesRequired",
  },
  // downloadReceiptsMockedValidationObj
  downloadReceiptsMonthMin: {
    id: "downloadReceiptsValidation.monthMin",
  },
  downloadReceiptsMonthMax: {
    id: "downloadReceiptsValidation.monthMax",
  },
  downloadReceiptsMonthRequired: {
    id: "downloadReceiptsValidation.monthRequired",
  },
  downloadReceiptsYearMin: {
    id: "downloadReceiptsValidation.yearMin",
  },
  downloadReceiptsYearMax: {
    id: "downloadReceiptsValidation.yearMax",
  },
  downloadReceiptsYearRequired: {
    id: "downloadReceiptsValidation.yearRequired",
  },
  escalationSupportRequired: {
    id: "escalation.topicRequired",
  },
  escalationMessageRequired: {
    id: "escalation.userMessageRequired",
  },
  escalationMessageMax: {
    id: "escalation.userMessageMax",
  },
  escalationMessageMin: {
    id: "escalation.userMessageMin",
  },
  escalationMessageMatches: {
    id: "escalation.userMessageMatches",
  },
  maintenanceTitleRequired: {
    id: "adminMaintenance.titleRequired",
  },
  maintenanceTitleMax: {
    id: "adminMaintenance.titleMax",
  },
  maintenanceTitleMin: {
    id: "adminMaintenance.titleMin",
  },
  maintenanceTitleMatches: {
    id: "adminMaintenance.titleMatches",
  },
  maintenanceMessageRequired: {
    id: "adminMaintenance.messageRequired",
  },
  maintenanceMessageMax: {
    id: "adminMaintenance.messageMax",
  },
  maintenanceMessageMin: {
    id: "adminMaintenance.messageMin",
  },
  // ucLoginMockedApiValidationObj
  ucSsoEmailMin: {
    id: "ucSsoEmailValidation.emailMin",
  },
  ucSsoEmailMax: {
    id: "ucSsoEmailValidation.emailMax",
  },
  ucSsoEmailMatches: {
    id: "ucSsoEmailValidation.emailMatches",
  },
  ucSsoEmailRequired: {
    id: "ucSsoEmailValidation.emailRequired",
  },
  mileagePlusNumberMin: {
    id: "validation.mileagePlusNumberMin",
  },
  mileagePlusNumberMax: {
    id: "validation.mileagePlusNumberMax",
  },
  mileagePlusNumberMatches: {
    id: "validation.mileagePlusNumberMatches",
  },
  mileagePlusNumberRequired: {
    id: "validation.mileagePlusNumberRequired",
  },
  createPolicyNameMin: {
    id: "createPolicyValidation.nameMin",
  },
  createPolicyNameMax: {
    id: "createPolicyValidation.nameMax",
  },
  createPolicyNameMatches: {
    id: "createPolicyValidation.nameMatches",
  },
  createPolicyNameRequired: {
    id: "createPolicyValidation.nameRequired",
  },
  editPolicyNameMin: {
    id: "editPolicyValidation.nameMin",
  },
  editPolicyNameMax: {
    id: "editPolicyValidation.nameMax",
  },
  editPolicyNameMatches: {
    id: "editPolicyValidation.nameMatches",
  },
  editPolicyNameRequired: {
    id: "editPolicyValidation.nameRequired",
  },
  editHotelPolicyMaxPriceMax: {
    id: "editHotelPolicyValidation.maxPriceMax",
  },
  policyLocationRequired: {
    id: "specificPolicy.locationRequired",
  },

  //addCitiEmailMockedApiValidationObj
  addCitiEmailMin: {
    id: "addCitiEmailValidation.emailMin",
  },
  addCitiEmailMax: {
    id: "addCitiEmailValidation.emailMax",
  },
  addCitiEmailMatches: {
    id: "addCitiEmailValidation.emailMatches",
  },
  addCitiEmailRequired: {
    id: "addCitiEmailValidation.emailRequired",
  },

  //
  citiCompanyEmailMin: {
    id: "citiCompanyEmailValidation.emailMin",
  },
  citiCompanyEmailMax: {
    id: "citiCompanyEmailValidation.emailMax",
  },
  citiCompanyEmailMatches: {
    id: "citiCompanyEmailValidation.emailMatches",
  },
  citiCompanyEmailRequired: {
    id: "citiCompanyEmailValidation.emailRequired",
  },

  //editLepTier
  pointValueMin: {
    id: "editTierModal.pointValueMin",
  },
  pointValueMatches: {
    id: "editTierModal.pointValueMatches",
  },
  pointValueRequired: {
    id: "editTierModal.pointValueRequired",
  },
  //
  entityPCAN: {
    id: "companyInfoValidation.entityPCAN",
  },
  entityPCANMin: {
    id: "companyInfoValidation.entityPCANMin",
  },
  entityPCANMax: {
    id: "companyInfoValidation.entityPCANMax",
  },
  entityPCANMatches: {
    id: "companyInfoValidation.entityPCANMatches",
  },
  citiPCANMatches: {
    id: "companyInfoValidation.citiPCANMatches",
  },

  //editProfileValidationObjObj
  editInputModalFieldMin: {
    id: "edit.UC.input.modal.fieldMin"
  },
  editInputModalFieldMax: {
    id: "edit.UC.input.modal.fieldMax"
  },
  editInputModalFieldMatches: {
    id: "edit.UC.input.modal.fieldMatches"
  },
  editInputModalFieldRequired: {
    id: "edit.UC.input.modal.fieldRequired"
  },
  //fareAccessValidation
  contractIdValueRequired:{
    id:"fareAccessValidation.contractIdValue.required"
  },
  contractIdValueMin:{
    id:"fareAccessValidation.contractIdValue.min"
  },
  contractIdValueMax:{
    id:"fareAccessValidation.contractIdValue.max"
  },
  contractIdValueMatches:{
    id:"fareAccessValidation.contractIdValue.matches"
  },

  // addBookingFieldMockedValidationObj
  bookingFieldNameMatches: {
    id: "addBookingFieldValidation.fieldMatches",
  },
  bookingFieldNameRequired: {
    id: "addBookingFieldValidation.fieldRequired",
  },
  bookingMinLengthMatches: {
    id: "addBookingFieldValidation.minLengthMatches",
  },
  bookingMinLengthRequired: {
    id: "addBookingFieldValidation.minLengthRequired",
  },
  bookingMaxLengthMatches: {
    id: "addBookingFieldValidation.maxLengthMatches",
  },
  bookingMaxLengthRequired: {
    id: "addBookingFieldValidation.maxLengthRequired",
  },

  //loyalty notification banner
  notificationBannerTitleMin: {
    id: "notificationBanner.titleMin",
  },
  notificationBannerTitleMax: {
    id: "notificationBanner.titleMax",
  },
  notificationBannerTitleMatches: {
    id: "notificationBanner.titleMatches",
  },
  notificationBannerTitleRequired: {
    id: "notificationBanner.titleRequired",
  },
  notificationBannerMessageMin: {
    id: "notificationBanner.messageMin",
  },
  notificationBannerMessageMax: {
    id: "notificationBanner.messageMax",
  },
  notificationBannerMessageMatches: {
    id: "notificationBanner.messageMatches",
  },
  notificationBannerMessageRequired: {
    id: "notificationBanner.messageRequired",
  },
  //overrideDiscountValidationObj
  discountCodeModalMin:{
    id:"overrideDiscountTierModal.validation.discountCodeMin"
  },
  discountCodeModalMax:{
    id:"overrideDiscountTierModal.validation.discountCodeMax"
  },
  discountCodeModalMatches:{
    id:"overrideDiscountTierModal.validation.discountCodeMatches"
  },

  // Hawaiian Account Number
  hawaiianNumberRequired: {
    id: "companyInfoValidation.hawaiianNumberRequired",
  },
  hawaiianNumberMatches: {
    id: "companyInfoValidation.hawaiianNumberMatches",
  },
  haCorporateProgramEnrolledRequired: {
    id: "companyInfoValidation.haCorporateProgramEnrolledRequired",
  },

  // Alaska Account Number
  businessAccountNumberRequired: {
    id: "companyInfoValidation.businessAccountNumberRequired",
  },
  businessAccountNumberMinLength: {
    id: "companyInfoValidation.businessAccountNumberMinLength",
  },

  // Alaska Referral Code
  referralCodeNumberMaxLength: {
    id: "companyInfoValidation.referralCodeNumberMaxLength"
  },
  referralCodeNumberMatches: {
    id: "companyInfoValidation.referralCodeNumberMatches"
  },

  // editLabelMockedValidationObj
  editLabelMin: {
    id: "editLabelValidation.nameMin",
  },
  editLabelMax: {
    id: "editLabelValidation.nameMax",
  },
  editLabelMatches: {
    id: "editLabelValidation.nameMatches",
  },
  editLabelRequired: {
    id: "editLabelValidation.nameRequired",
  },
  // createGuestProfileFormValidationObj
  guestFirstNameMin: {
    id: "guest.firstNameMin",
  },
  guestFirstNameMax: {
    id: "guest.firstNameMax",
  },
  guestFirstNameMatches: {
    id: "guest.firstNameMatches",
  },
  guestFirstNameRequired: {
    id: "guest.firstNameRequired",
  },
  guestLastNameMin: {
    id: "guest.lastNameMin",
  },
  guestLastNameMax: {
    id: "guest.lastNameMax",
  },
  guestLastNameMatches: {
    id: "guest.lastNameMatches",
  },
  guestLastNameRequired: {
    id: "guest.lastNameRequired",
  },
  guestMiddleNameMin: {
    id: "guest.middleNameMin",
  },
  guestMiddleNameMax: {
    id: "guest.middleNameMax",
  },
  guestMiddleNameMatches: {
    id: "guest.middleNameMatches",
  },
  guestSuffixMatches: {
    id: "guest.suffixMatches"
  },
  guestSuffixMax: {
    id: "guest.suffixMax"
  },
  guestDobMinMaxMatches: {
    id: "guest.dobMinMaxMatches",
  },
  guestDobRequired: {
    id: "guest.dobRequired",
  },
  guestGenderRequired: {
    id: "guest.genderRequired"
  },
  guestPreferredNameMin: {
    id: "guest.preferredNameMin",
  },
  guestPreferredNameMax: {
    id: "guest.preferredNameMax",
  },
  guestPreferredNameMatches: {
    id: "guest.preferredNameMatches",
  },
  guestEmailMin: {
    id: "guest.emailMin",
  },
  guestEmailMax: {
    id: "guest.emailMax",
  },
  guestPhoneRequired: {
    id: "guest.phoneRequired"
  },
  guestKtnMatches: {
    id: "guest.ktnMatches",
  },
  guestKtnLength: {
    id: "guest.ktnLength"
  },
  guestRedressMatches: {
    id: "guest.redressMatches"
  },
  guestRedressMin: {
    id: "guest.redressMin"
  },
  guestRedressMax: {
    id: "guest.redressMax"
  },
  guestEmailMatches: {
    id: "guest.emailMatches",
  },
  guestMembershipNumberMatches: {
    id: "guest.membershipNumberMatches",
  },
  guestMembershipNumberMin: {
    id: "guest.membershipNumberMin",
  },
  guestMembershipNumberMax: {
    id: "guest.membershipNumberMax",
  },
  guestExpirationDateMinMaxMatches: {
    id: "guest.expiresMinMaxMatches"
  },
  companyBusinessWebsiteMatches: {
    id: "companyInfoValidation.businessWebsiteMatches",
  },
  companyReferralCodeMatches: {
    id: "companyInfoValidation.referralCodeMatches",
  },
  companyPromoCodes: {
    id: "companyInfoValidation.promoCodes"
  }
});

export const useValidationService = () => {
  const intl = useIntl();

  const specificPolicyMockedApiValidationObj = [
    {
      "fieldName": "fromLocationName",
      "validations": {
        "validators": {
          "required": true,
        },
        "msgs": {
          "required": intl.formatMessage(validationMessages.policyLocationRequired)
        }
      },
    },
    {
      "fieldName": "toLocationName",
      "validations": {
        "validators": {
          "required": true,
        },
        "msgs": {
          "required": intl.formatMessage(validationMessages.policyLocationRequired)
        }
      },
    },
  ]

  const maintenanceFormValidationObj = [
    {
      "fieldName": "title",
      "validations": {
        "validators": {
          "required": true,
          "matches": "^$|^(?!\\s)(?![\\s\\S]*\\s$)[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\-']+(s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\- '.,()?!]+)*$",
          "min": 10,
          "max": 60
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.maintenanceTitleMin),
          "max": intl.formatMessage(validationMessages.maintenanceTitleMax),
          "matches": intl.formatMessage(validationMessages.maintenanceTitleMatches),
          "required": intl.formatMessage(validationMessages.maintenanceTitleRequired)
        }
      },
    },
    {
      "fieldName": "message",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "matches": "^$|^(?!\\s)(?![\\s\\S]*\\s$)[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\-']+(s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\- '.,()?!]+)*$",
          "required": true,
          "min": 30,
          "max": 200
        },
        "msgs": {
          "max": intl.formatMessage(validationMessages.maintenanceMessageMax),
          "min": intl.formatMessage(validationMessages.maintenanceMessageMin),
          "matches": intl.formatMessage(validationMessages.maintenanceTitleMatches),
          "required": intl.formatMessage(validationMessages.maintenanceMessageRequired)
        }
      },
    },
  ]

  const createEscalationFormValidationObj = [
    {
      "fieldName": "supportTopic",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "required": true,
        },
        "msgs": {
          "required": intl.formatMessage(validationMessages.escalationSupportRequired)
        }
      },
    },
    {
      "fieldName": "userMessage",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "required": true,
          "max": 1500,
          "matches": "^(?!\\s)[^<>{}'\"&=%$]*$"
        },
        "msgs": {
          "max": intl.formatMessage(validationMessages.escalationMessageMax),
          "required": intl.formatMessage(validationMessages.escalationMessageRequired),
          "matches": intl.formatMessage(validationMessages.escalationMessageMatches)
        }
      },
    }
  ]


  const travelerDetailsMockedApiValidationObj = [
    {
      "fieldName": "firstName",
      "validations": {
        "validators": {
          "required": true,
          "matches": "^$|^[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\-']+(s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\- ']+)*$",
          "min": 1,
          "max": 99
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.travelerFirstNameMin),
          "max": intl.formatMessage(validationMessages.travelerFirstNameMax),
          "matches": intl.formatMessage(validationMessages.travelerFirstNameMatches),
          "required": intl.formatMessage(validationMessages.travelerFirstNameRequired)
        }
      },
    },
    {
      "fieldName": "lastName",
      "validations": {
        "validators": {
          "required": true,
          "matches": "^$|^[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\-']+(s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\- ']+)*$",
          "min": 1,
          "max": 99
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.travelerLastNameMin),
          "max": intl.formatMessage(validationMessages.travelerLastNameMax),
          "matches": intl.formatMessage(validationMessages.travelerLastNameMatches),
          "required": intl.formatMessage(validationMessages.travelerLastNameRequired)
        }
      },
    },
    {
      "fieldName": "emailAddress",
      "validations": {
        "validators": {
          "required": true,
          "matches": "^[a-zA-Z0-9][a-zA-Z0-9.!#$%&'*+=?^_`{|}~-]+@([a-zA-Z0-9-]+\\.)+[a-zA-Z0-9-]{2,100}$",
          "min": 1,
          "max": 99
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.travelerEmailMin),
          "max": intl.formatMessage(validationMessages.travelerEmailMax),
          "matches": intl.formatMessage(validationMessages.travelerEmailMatches),
          "required": intl.formatMessage(validationMessages.travelerEmailRequired)
        }
      },
    },
    {
      "fieldName": "roleId",
      "validations": {
        "validators": {
          "required": true,
          "min": 1,
          "max": 99
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.travelerRoleMin),
          "max": intl.formatMessage(validationMessages.travelerRoleMax),
          "required": intl.formatMessage(validationMessages.travelerRoleRequired)
        }
      },
    },
    {
      "fieldName": "groupId",
      "validations": {
        "validators": {
          "required": true,
          "min": 1,
          "max": 99
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.travelerTeamMin),
          "max": intl.formatMessage(validationMessages.travelerTeamMax),
          "required": intl.formatMessage(validationMessages.travelerTeamRequired)
        }
      },
    }
  ]

  const editTeamMockedValidationObj = [
    {
      "fieldName": "groupName",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "required": true,
          "matches": "^$|^[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\-']+(\s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\- ']+)*$",
          "min": 3,
          "max": 25
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.editTeamNameMin),
          "max": intl.formatMessage(validationMessages.editTeamNameMax),
          "matches": intl.formatMessage(validationMessages.editTeamNameMatches),
          "required": intl.formatMessage(validationMessages.editTeamNameRequired)
        }
      },
    }
  ]

  const editLabelMockedValidationObj = [
    {
      "fieldName": "name",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "required": true,
          "matches": "^$|^[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0.,!@$*();:?&#'-]+(s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0.,!@$*();:?&#'-]+)*$",
          "min": 3,
          "max": 25
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.editLabelMin),
          "max": intl.formatMessage(validationMessages.editLabelMax),
          "matches": intl.formatMessage(validationMessages.editLabelMatches),
          "required": intl.formatMessage(validationMessages.editLabelRequired)
        }
      },
    }
  ]

  const createTeamMockedValidationObj = [
    {
      "fieldName": "groupName",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "required": true,
          "matches": "^$|^[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ\\-']+(\\s*[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ\\-']+)*$",
          "min": 3,
          "max": 25
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.createTeamNameMin),
          "max": intl.formatMessage(validationMessages.createTeamNameMax),
          "matches": intl.formatMessage(validationMessages.createTeamNameMatches),
          "required": intl.formatMessage(validationMessages.createTeamNameRequired)
        }
      },
    }
  ]

  const companyInfoMockedApiValidationObj = [
    {
      "fieldName": "companyName",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "required": true,
          "matches": "^$|^[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0.,!@$*();:?&#'-]+(s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0.,!@$*();:?&#'-]+)*$",
          "min": 1,
          "max": 255
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.companyNameMin),
          "max": intl.formatMessage(validationMessages.companyNameMax),
          "matches": intl.formatMessage(validationMessages.companyNameMatches),
          "required": intl.formatMessage(validationMessages.companyNameRequired)
        }
      },
    },
    // {
    //   "fieldName": "companySize",
    //   "validations": {
    //     // "skipValidation": true,
    //     "validators": {
    //       "required": true,
    //       "matches": "^$|^[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\-']+(s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\- ']+)*$",
    //       "min": 1,
    //       "max": 20
    //     },
    //     "msgs": {
    //       "min": "{0} too short",
    //       "max": "{0} too long",
    //       "matches": "{0} can contain letter,numbers,space or special characters and cannot start with space",
    //       "required": "{0} is required"
    //     }
    //   },
    // },
    {
      "fieldName": "phoneNumber",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "required": true,
          "matches": "^$|^[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\-']+(s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\- ']+)*$",
        },
        "msgs": {
          "matches": intl.formatMessage(validationMessages.companyPhoneMatches),
          "required": intl.formatMessage(validationMessages.companyPhoneRequired)
        }
      },
    },
    {
      "fieldName": "countryCode",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "required": true,
        },
        "msgs": {
          "required": intl.formatMessage(validationMessages.companyCountryRequired)
        }
      },
    },
    {
      "fieldName": "phoneCountryCode",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "required": true,
        },
        "msgs": {
          "required": intl.formatMessage(validationMessages.companyCountryRequired)
        }
      },
    },
    {
      "fieldName": "streetAddress1",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "required": true,
          "matches": "^$|^[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\-']+(s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\-#&/()., ']+)*$",
          "min": 1,
          "max": 250
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.companyFirstAddressMin),
          "max": intl.formatMessage(validationMessages.companyFirstAddressMax),
          "matches": intl.formatMessage(validationMessages.companyFirstAddressMatches),
          "required": intl.formatMessage(validationMessages.companyFirstAddressRequired)
        }
      },
    },
    {
      "fieldName": "streetAddress2",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "required": false,
          "matches": "^$|^[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\-']+(s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\-#&/()., ']+)*$",
          "min": 1,
          "max": 250
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.companySecondAddressMin),
          "max": intl.formatMessage(validationMessages.companySecondAddressMax),
          "matches": intl.formatMessage(validationMessages.companySecondAddressMatches),
          "required": intl.formatMessage(validationMessages.companySecondAddressRequired)
        }
      },
    },
    {
      "fieldName": "city",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "required": true,
          "matches": "^$|^[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\-']+(s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\- ']+)*$",
          "min": 1,
          "max": 100
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.companyCityMin),
          "max": intl.formatMessage(validationMessages.companyCityMax),
          "matches": intl.formatMessage(validationMessages.companyCityMatches),
          "required": intl.formatMessage(validationMessages.companyCityRequired)
        }
      },
    },
    {
      "fieldName": "state",
      "validations": {
        "validators": {
          "required": true,
          "matches": "[^ ]",
        },
        "msgs": {
          "matches": intl.formatMessage(validationMessages.companyStateRequired),
          "required": intl.formatMessage(validationMessages.companyStateRequired)
        }
      },
    },
    {
      "fieldName": "postalCode",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "required": true,
          "matches": "^[A-Za-z0-9 ]*$",
          "min": 5,
          "max": 20
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.companyPostalMin),
          "max": intl.formatMessage(validationMessages.companyPostalMax),
          "matches": intl.formatMessage(validationMessages.companyPostalMatches),
          "required": intl.formatMessage(validationMessages.companyPostalRequired)
        }
      },
    },
    {
      "fieldName": "previousAccountNumber",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "required": false,
          "matches": "^$|^[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\-']+(s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\-#&/()., ']+)*$",
        },
        "msgs": {
          "matches": intl.formatMessage(validationMessages.companyPreviousAccountMatches),
          "required": intl.formatMessage(validationMessages.companySecondAddressRequired)
        }
      },
    },
    {
      "fieldName": "companyStar",
      "validations": {
        "validators": {
          "required": false,
          "matches": "^[a-zA-Z0-9*-][a-zA-Z0-9 *-]*$",
          "min": 1,
          "max": 39
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.companyStarValidationError),
          "max": intl.formatMessage(validationMessages.companyStarValidationError),
          "matches": intl.formatMessage(validationMessages.companyStarValidationError)
        }
      },
    },
    {
      "fieldName": "businessWebsite",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "required": false,
          "matches": "^((ftp|http|https):\/\/)?(www.)?(?!.*(ftp|http|https|www.))[a-zA-Z0-9_-]+(\.[a-zA-Z]+)+((\/)[\w#]+)*(\/\w+\?[a-zA-Z0-9_]+=\w+(&[a-zA-Z0-9_]+=\w+)*)?\/?$",
        },
        "msgs": {
          "matches": intl.formatMessage(validationMessages.companyBusinessWebsiteMatches)
        }
      },
    },
    {
      "fieldName": "referralCode",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "required": false,
          "matches": "^$|^[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\-']+(s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\-#&/()., ']+)*$",
        },
        "msgs": {
          "matches": intl.formatMessage(validationMessages.companyReferralCodeMatches)
        }
      },
    },
    {
      "fieldName": 'promoCodes',
      "type": 'array',
      "validations": {
        "validators": {
          "required": false,
          "eachItemMatch": '^[a-zA-Z0-9]{6}$', // Regex for 6 alphanumeric characters
        },
        "msgs": {
          "eachItemMatch": intl.formatMessage(validationMessages.companyPromoCodes)
        },
      },
    }
  ]

  const companyInfoMockedApiValidationObjAM = [
    ...companyInfoMockedApiValidationObj,
    {
      fieldName: 'businessAccountNumber',
      validations: {
        validators: {
          required: true,
          min: 1,
        },
        msgs: {
          required: intl.formatMessage(validationMessages.businessAccountNumberRequired),
          min: intl.formatMessage(validationMessages.businessAccountNumberMinLength),
        },
      },
    },
    {
      fieldName: 'referralCode',
      validations: {
        validators: {
          min: 0,
          max: 10,
          matches: '^[a-zA-Z0-9]{0,10}$',
        },
        msgs: {
          min: intl.formatMessage(validationMessages.referralCodeNumberMaxLength),
          max: intl.formatMessage(validationMessages.referralCodeNumberMaxLength),
          matches: intl.formatMessage(validationMessages.referralCodeNumberMatches),
        },
      },
    },
  ]

  const companyInfoMockedApiValidationObjAS = [
    ...companyInfoMockedApiValidationObj,
    {
      fieldName: 'hawaiianNumber',
      validations: {
        validators: {
          when: ["haCorporateProgramEnrolled", {
            is: 'true',
            then: {
              validations: {
                validators: {
                  required: true,
                  matches: '^(191|194|196)[0-9]{6}$',
                },
                msgs: {
                  required: intl.formatMessage(validationMessages.hawaiianNumberRequired),
                  matches: intl.formatMessage(validationMessages.hawaiianNumberMatches),
                }
              }
            },
            otherwise: {
              validations: {
                validators: {
                  transform: ""
                }
              }
            },
          }]
        },
        msgs: {}
      },
    },
    {
      fieldName: 'haCorporateProgramEnrolled',
      validations: {
        validators: {
          required: true,
        },
        msgs: {
          required: intl.formatMessage(validationMessages.haCorporateProgramEnrolledRequired),
        }
      },
    },
    {
      fieldName: 'businessAccountNumber',
      validations: {
        validators: {
          required: true,
          min: 1,
        },
        msgs: {
          required: intl.formatMessage(validationMessages.businessAccountNumberRequired),
          min: intl.formatMessage(validationMessages.businessAccountNumberMinLength),
        },
      },
    },
    {
      fieldName: 'referralCode',
      validations: {
        validators: {
          min: 0,
          max: 10,
          matches: '^[a-zA-Z0-9]{0,10}$',
                  },
        msgs: {
          min: intl.formatMessage(validationMessages.referralCodeNumberMaxLength),
          max: intl.formatMessage(validationMessages.referralCodeNumberMaxLength),
          matches: intl.formatMessage(validationMessages.referralCodeNumberMatches),
        },
      },
    },
  ]

    const companyInfoMockedApiValidationObjChoice = [
    ...companyInfoMockedApiValidationObj,
    {
      "fieldName": "industryType",
      "validations": {
        "validators": {
          "required": false,
        },
        "msgs": {
          "required": intl.formatMessage(validationMessages.companyFieldRequired),
        },
      },
    },
    {
      "fieldName": "hotelExpenditure",
      "validations": {
        "validators": {
          "required": false,
        },
        "msgs": {
          "required": intl.formatMessage(validationMessages.companyFieldRequired),
        },
      },
    },
    {
      "fieldName": "hearAboutUs",
      "validations": {
        "validators": {
          "required": false,
        },
        "msgs": {
          "required": intl.formatMessage(validationMessages.companyFieldRequired),
        },
      },
    },
    {
      "fieldName": "hearAboutUsOther",
      "validations": {
        "validators": {
          "matches": "^[^\"/\\\\()]*$",
          // if this will be turn on, check form behaviour as this field is initially hidden and the submit will not work because of the hidden error
          "required": false,
        },
        "msgs": {
          "matches": intl.formatMessage(validationMessages.companyFieldMatches),
          "required": intl.formatMessage(validationMessages.companyFieldRequired),
        },
      },
    },
    {
      "fieldName": "numberOfEmployees",
      "validations": {
        "validators": {
          "required": false,
        },
        "msgs": {
          "required": intl.formatMessage(validationMessages.companyFieldRequired),
        },
      },
    },
  ]

    const companyInfoMockedApiValidationObjAC = [
      ...companyInfoMockedApiValidationObj,
      {
        fieldName: 'industryType',
        validations: {
          validators: {
            required: false,
          },
          msgs: {
            required: intl.formatMessage(validationMessages.companyFieldRequired),
          },
        },
      },
      {
        fieldName: 'hotelExpenditure',
        validations: {
          validators: {
            required: false,
          },
          msgs: {
            required: intl.formatMessage(validationMessages.companyFieldRequired),
          },
        },
      },
      {
        fieldName: 'hearAboutUs',
        validations: {
          validators: {
            required: false,
          },
          msgs: {
            required: intl.formatMessage(validationMessages.companyFieldRequired),
          },
        },
      },
      {
        fieldName: 'hearAboutUsOther',
        validations: {
          validators: {
            matches: '^[^"/\\\\()]*$',
            // if this will be turn on, check form behaviour as this field is initially hidden and the submit will not work because of the hidden error
            required: false,
          },
          msgs: {
            matches: intl.formatMessage(validationMessages.companyFieldMatches),
            required: intl.formatMessage(validationMessages.companyFieldRequired),
          },
        },
      },
      {
        fieldName: 'numberOfEmployees',
        validations: {
          validators: {
            required: false,
          },
          msgs: {
            required: intl.formatMessage(validationMessages.companyFieldRequired),
          },
        },
      },
    ];

  const invitePeopleMockedApiValidationObj = [
    {
      "fieldName": "emailAddresses",
      "type": "array",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "required": true,
          "emails": "^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$",
          "min": 1,
          "max": 20
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.inviteEmailMin),
          "max": intl.formatMessage(validationMessages.inviteEmailMax),
          "emails": intl.formatMessage(validationMessages.inviteEmailMatches),
          "required": intl.formatMessage(validationMessages.inviteEmailRequired)
        }
      }
    },
    {
      "fieldName": "role",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "required": true,
        },
        "msgs": {
          "required": intl.formatMessage(validationMessages.inviteRoleRequired)
        }
      }
    },
  ]

  const inviteAuthorisedMockedValidationObj = [
    {
      "fieldName": "shellUsers",
      "type": "array",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "required": true,
          "email": "^[a-zA-Z0-9][a-zA-Z0-9.!#$%&'*+=?^_`{|}~-]+@([a-zA-Z0-9-]+\\.)+[a-zA-Z0-9-]{2,100}$",
          "min": 1,
          "max": 99
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.inviteAuthEmailMin),
          "max": intl.formatMessage(validationMessages.inviteAuthEmailMax),
          "email": intl.formatMessage(validationMessages.inviteAuthEmailMatches),
          "required": intl.formatMessage(validationMessages.inviteAuthEmailRequired)
        }
      }
    }
  ]

  const addDomainMockedValidationObj = [
    {
      "fieldName": "domain",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "required": true,
          "matches": /^(?!-)[A-Za-záéíñóúüÁÉÍÑÓÚÜ0-9-]{1,63}(?<!-)(\.(?!-)[A-Za-záéíñóúüÁÉÍÑÓÚÜ0-9-]{1,63}(?<!-))*\.[A-Za-z]{2,}$/,
          "min": 3,
          "max": 100
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.addDomainMin),
          "max": intl.formatMessage(validationMessages.addDomainMax),
          "matches": intl.formatMessage(validationMessages.addDomainMatches),
          "required": intl.formatMessage(validationMessages.addDomainRequired)
        }
      },
    },
    {
      "fieldName": "emailAddress",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "required": true,
          "matches": "^(?![.])[a-zA-Z0-9.!#$%&'*+=?^_`{|}~-]+@([a-zA-Z0-9-]+\\.)+[a-zA-Z0-9-]{2,100}$",
          "min": 3,
          "max": 320
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.addDomainEmailMin),
          "max": intl.formatMessage(validationMessages.addDomainEmailMax),
          "matches": intl.formatMessage(validationMessages.addDomainEmailMatches),
          "required": intl.formatMessage(validationMessages.addDomainEmailRequired)
        }
      },
    }
  ]

  const tranferMilesMockedValidationObj = [
    {
      "fieldName": "miles",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "required": false,
          "matches": "^(?!0)[0-9,]+$",
          "min": 1,
          // Note: max validation value set in component
          "max": null
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.transferMilesMin),
          "max": "",
          "matches": intl.formatMessage(validationMessages.transferMilesMatches),
          "required": intl.formatMessage(validationMessages.transferMilesRequired)
        }
      },
    }
  ]

  const downloadReceiptsMockedValidationObj = [
    {
      "fieldName": "month",
      "validations": {
        "validators": {
          "required": true,
          "min": 1,
          "max": 99
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.downloadReceiptsMonthMin),
          "max": intl.formatMessage(validationMessages.downloadReceiptsMonthMax),
          "required": intl.formatMessage(validationMessages.downloadReceiptsMonthRequired)
        }
      },
    },
    {
      "fieldName": "year",
      "validations": {
        "validators": {
          "required": true,
          "min": 1,
          "max": 99
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.downloadReceiptsYearMin),
          "max": intl.formatMessage(validationMessages.downloadReceiptsYearMax),
          "required": intl.formatMessage(validationMessages.downloadReceiptsYearRequired)
        }
      },
    },
  ]

  const inviteGuestMockedApiValidationObj = [
    {
      "fieldName": "emailAddress",
      "validations": {
        "validators": {
          "required": true,
          "matches": "^[a-zA-Z0-9][a-zA-Z0-9.!#$%&'*+=?^_`{|}~-]+@([a-zA-Z0-9-]+\\.)+[a-zA-Z0-9-]{2,100}$",
          "min": 1,
          "max": 99
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.travelerEmailMin),
          "max": intl.formatMessage(validationMessages.travelerEmailMax),
          "matches": intl.formatMessage(validationMessages.travelerEmailMatches),
          "required": intl.formatMessage(validationMessages.travelerEmailRequired)
        }
      },
    },
    {
      "fieldName": "groupId",
      "validations": {
        "validators": {
          "required": true,
          "min": 1,
          "max": 99
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.travelerTeamMin),
          "max": intl.formatMessage(validationMessages.travelerTeamMax),
          "required": intl.formatMessage(validationMessages.travelerTeamRequired)
        }
      },
    }
  ]
  const addCitiEmailMockedApiValidationObj = [
    {
      "fieldName": "emailAddress",
      "validations": {
        "validators": {
          "required": true,
          "matches": "^[a-zA-Z0-9][a-zA-Z0-9.!#$%&'*+=?^_`{|}~-]+@([a-zA-Z0-9-]+\\.)+[a-zA-Z0-9-]{2,100}$",
          "min": 1,
          "max": 99
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.addCitiEmailMin),
          "max": intl.formatMessage(validationMessages.addCitiEmailMax),
          "matches": intl.formatMessage(validationMessages.addCitiEmailMatches),
          "required": intl.formatMessage(validationMessages.addCitiEmailRequired)
        }
      },
    },
  ]

  const updateEmailMockedApiValidationObj = [
    {
      "fieldName": "emailAddress",
      "validations": {
        "validators": {
          "required": true,
          "matches": "^[a-zA-Z0-9][a-zA-Z0-9.!#$%&'*+=?^_`{|}~-]+@([a-zA-Z0-9-]+\\.)+[a-zA-Z0-9-]{2,100}$",
          "min": 1,
          "max": 99
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.travelerEmailMin),
          "max": intl.formatMessage(validationMessages.travelerEmailMax),
          "matches": intl.formatMessage(validationMessages.travelerEmailMatches),
          "required": intl.formatMessage(validationMessages.travelerEmailRequired)
        }
      },
    },
  ]

  const ucLoginMockedApiValidationObj = [
    {
      "fieldName": "loginEmail",
      "validations": {
        "validators": {
          "required": true,
          "matches": "^[a-zA-Z0-9][a-zA-Z0-9.!#$%&'*+=?^_`{|}~-]+@([a-zA-Z0-9-]+\\.)+[a-zA-Z0-9-]{2,100}$",
          "min": 1,
          "max": 99
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.ucSsoEmailMin),
          "max": intl.formatMessage(validationMessages.ucSsoEmailMax),
          "matches": intl.formatMessage(validationMessages.ucSsoEmailMatches),
          "required": intl.formatMessage(validationMessages.ucSsoEmailRequired)
        }
      },
    },
    // {
    //   "fieldName": "loginPassword",
    //   "validations": {
    //     "validators": {
    //       "required": true,
    //       "matches": "",
    //       "min": 1,
    //       "max": 99
    //     },
    //     "msgs": {
    //       "min": intl.formatMessage(validationMessages.ucSsoPasswordMin),
    //       "max": intl.formatMessage(validationMessages.ucSsoPasswordMax),
    //       "matches": intl.formatMessage(validationMessages.ucSsoPasswordMatches),
    //       "required": intl.formatMessage(validationMessages.ucSsoPasswordRequired)
    //     }
    //   },
    // },
  ]

  const ucJoinUnitedMockedApiValidationObj = [
    {
      "fieldName": "firstName",
      "validations": {
        "validators": {
          "required": true,
          "matches": "^$|^[a-zA-Z\\u00C0-\\u024F]+([' -][a-zA-Z\\u00C0-\\u024F]+)*$",
          "min": 2,
          "max": 50
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.travelerFirstNameMin),
          "max": intl.formatMessage(validationMessages.travelerFirstNameMax),
          "matches": intl.formatMessage(validationMessages.travelerFirstNameUCJoinMatches),
          "required": intl.formatMessage(validationMessages.travelerFirstNameRequired)
        }
      },
    },
    {
      "fieldName": "lastName",
      "validations": {
        "validators": {
          "required": true,
           "matches": "^$|^[a-zA-Z\\u00C0-\\u024F]+([' -][a-zA-Z\\u00C0-\\u024F]+)*$",
          "min": 2,
          "max": 50
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.travelerLastNameMin),
          "max": intl.formatMessage(validationMessages.travelerLastNameMax),
          "matches": intl.formatMessage(validationMessages.travelerLastNameUCJoinMatches),
          "required": intl.formatMessage(validationMessages.travelerLastNameRequired)
        }
      },
    },
    {
      "fieldName": "membershipNumber",
      "validations": {
        "validators": {
          "required": true,
          "matches": "^[a-zA-Z0-9]+$",
          "min": 1,
          "max": 255
        },
        "msgs": {
          "min": intl.formatMessage({ id: "validation.mileagePlusNumberMin" }),
          "max": intl.formatMessage({ id: "validation.mileagePlusNumberMax" }),
          "matches": intl.formatMessage({ id: "validation.mileagePlusNumberMatches" }),
          "required": intl.formatMessage({ id: "validation.mileagePlusNumberRequired" })
        }
      },
    }
  ];
  const createPolicyMockedValidationObj = [
    {
      "fieldName": "policyName",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "required": true,
          "matches": "^$|^[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\-']+(s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\- ']+)*$",
          "min": 3,
          "max": 25
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.createPolicyNameMin),
          "max": intl.formatMessage(validationMessages.createPolicyNameMax),
          "matches": intl.formatMessage(validationMessages.createPolicyNameMatches),
          "required": intl.formatMessage(validationMessages.createPolicyNameRequired)
        }
      },
    }
  ]

  const editPolicyMockedValidationObj = [
    {
      "fieldName": "policyName",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "required": true,
          "matches": "^$|^[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\-']+(s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\- ']+)*$",
          "min": 3,
          "max": 25
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.editPolicyNameMin),
          "max": intl.formatMessage(validationMessages.editPolicyNameMax),
          "matches": intl.formatMessage(validationMessages.editPolicyNameMatches),
          "required": intl.formatMessage(validationMessages.editPolicyNameRequired)
        }
      },
    }
  ]

  const editHotelPolicyMockedValidationObj = [
    {
      "fieldName": "maxPricePerNight",
      "validations": {
        "validators": {
          "matches": "^$|^[0-9]{1,5}$"
        },
        "msgs": {
          "matches": intl.formatMessage(validationMessages.editHotelPolicyMaxPriceMax)
        }
      },
    }
  ]

  const ucJoinDeltaMockedApiValidationObj = [
    {
      "fieldName": "firstName",
      "validations": {
        "validators": {
          "required": true,
          "matches": "^$|^[a-zA-Z\\u00C0-\\u024F]+([' -][a-zA-Z\\u00C0-\\u024F]+)*$",
          "min": 2,
          "max": 50
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.travelerFirstNameMin),
          "max": intl.formatMessage(validationMessages.travelerFirstNameMax),
          "matches": intl.formatMessage(validationMessages.travelerFirstNameUCJoinMatches),
          "required": intl.formatMessage(validationMessages.travelerFirstNameRequired)
        }
      }
    },
    {
      "fieldName": "lastName",
      "validations": {
        "validators": {
          "required": true,
          "matches": "^$|^[a-zA-Z\\u00C0-\\u024F]+([' -][a-zA-Z\\u00C0-\\u024F]+)*$",
          "min": 2,
          "max": 50
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.travelerLastNameMin),
          "max": intl.formatMessage(validationMessages.travelerLastNameMax),
          "matches": intl.formatMessage(validationMessages.travelerLastNameUCJoinMatches),
          "required": intl.formatMessage(validationMessages.travelerLastNameRequired)
        }
      }
    },
    {
      "fieldName": "membershipNumber",
      "validations": {
        "validators": {
          "required": true,
          "matches": "^[a-zA-Z0-9]+$",
          "min": 1,
          "max": 255
        },
        "msgs": {
          "min": intl.formatMessage({ id: "validation.skyMilesNumberMin" }),
          "max": intl.formatMessage({ id: "validation.skyMilesNumberMax" }),
          "matches": intl.formatMessage({ id: "validation.skyMilesNumberMatches" }),
          "required": intl.formatMessage({ id: "validation.skyMilesNumberRequired" })
        }
      }
    },
    {
      "fieldName": "acceptTerms",
      "validations": {
        "validators": {
          "required": true
        },
        "msgs": {
          "required": intl.formatMessage({ id: "validation.acceptTermsRequired" })
        }
      }
    },
    {
      "fieldName": "acceptPrivacyPolicy",
      "validations": {
        "validators": {
          "required": true
        },
        "msgs": {
          "required": intl.formatMessage({ id: "validation.acceptPrivacyPolicyRequired" })
        }
      }
    }
  ];

  const updateCitiEmailMockedApiValidationObj = [
    {
      "fieldName": "emailAddress",
      "validations": {
        "validators": {
          "required": true,
          "matches": "^[a-zA-Z0-9][a-zA-Z0-9.!#$%&'*+=?^_`{|}~-]+@([a-zA-Z0-9-]+\\.)+[a-zA-Z0-9-]{2,100}$",
          "min": 1,
          "max": 99
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.citiCompanyEmailMin),
          "max": intl.formatMessage(validationMessages.citiCompanyEmailMax),
          "matches": intl.formatMessage(validationMessages.citiCompanyEmailMatches),
          "required": intl.formatMessage(validationMessages.citiCompanyEmailRequired)
        }
      },
    },
  ]

  const editLepTierMockedValidationObj = [
    {
      "fieldName": "pointValue",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "required": true,
          "matches": "^[0-9]{1,3}(,[0-9]{3})*(\s{0,}[0-9]+)*$",
          "min": 1,
          //custom min and max set in component
          "max": null
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.pointValueMin),
          "max": "",
          "matches": intl.formatMessage(validationMessages.pointValueMatches),
          "required": intl.formatMessage(validationMessages.pointValueRequired)
        }
      },
    }
  ]

  const updateCitiPCANMockedApiValidationObj = [
    {
      "fieldName": "entityPCAN",
      "type": "array",
      "validations": {
        "validators": {
          "required": false,
          "eachItemMatch": "^0000000[0-9]{9}$", // PCAN regex - 16 numeric, always 7 zeros first
        },
        "msgs": {
          "eachItemMatch": intl.formatMessage(validationMessages.citiPCANMatches)
        },
      },
    },
  ]

  const editCitiPCANMockedApiValidationObj = [
    {
      "fieldName": "primaryPCAN",
      "type": "array",
      "validations": {
        "validators": {
          "required": false,
          "eachItemMatch": "^0000000[0-9]{9}$", // PCAN regex - 16 numeric, always 7 zeros first
        },
        "msgs": {
          "eachItemMatch": intl.formatMessage(validationMessages.citiPCANMatches)
        },
      },
    },
    {
      "fieldName": "auPCAN",
      "type": "array",
      "validations": {
        "validators": {
          "required": false,
          "eachItemMatch": "^0000000[0-9]{9}$", // PCAN regex - 16 numeric, always 7 zeros first
        },
        "msgs": {
          "eachItemMatch": intl.formatMessage(validationMessages.citiPCANMatches)
        },
      },
    },
  ]

  const fareAccessValidationObj = [
    {
      "fieldName": "contractId",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "required": false,
          "matches": "^[^\\s][a-zA-Z0-9]*$",
          "min": 4                     ,
          "max": 8
        },
        "msgs": {
          "required": intl.formatMessage(validationMessages.contractIdValueRequired),
          "matches": intl.formatMessage(validationMessages.contractIdValueMatches),
          "min": intl.formatMessage(validationMessages.contractIdValueMin),
          "max": intl.formatMessage(validationMessages.contractIdValueMax),
        }
      }
    },
  ]

  const addBookingFieldMockedValidationObj = [
    {
      "fieldName": "fieldName",
      "validations": {
        "validators": {
          "required": true,
          "matches": "^$|^[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0-9]+(s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0-9.!#@$&' ,;:*()+\\-_`|~\"^]+)*$",
        },
        "msgs": {
          "matches": intl.formatMessage(validationMessages.bookingFieldNameMatches),
          "required": intl.formatMessage(validationMessages.bookingFieldNameRequired)
        }
      },
    },
    {
      "fieldName": "minLength",
      "validations": {
        "validators": {
          "required": true,
          "matches": "^(?:[1-9]|1[0-9]|2[0-9]|30)$",
        },
        "msgs": {
          "matches": intl.formatMessage(validationMessages.bookingMinLengthMatches),
          "required": intl.formatMessage(validationMessages.bookingMinLengthRequired)
        }
      },
    },
    {
      "fieldName": "maxLength",
      "validations": {
        "validators": {
          "required": true,
          "matches": "^(?:[1-9]|1[0-9]|2[0-9]|30)$",
        },
        "msgs": {
          "matches": intl.formatMessage(validationMessages.bookingMaxLengthMatches),
          "required": intl.formatMessage(validationMessages.bookingMaxLengthRequired)
        }
      },
    }
  ]
  const overrideDiscountTierApiValidationObj = [
    {
      "fieldName": "discountCode",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "required": false,
          "matches": "^(?! )[A-Za-z0-9]{1,6}$",
          "min": 6,
          "max": 6,
        },
        "msgs": {
          "required": intl.formatMessage(validationMessages.discountCodeModalMin),
          "matches": intl.formatMessage(validationMessages.discountCodeModalMatches),
          "min": intl.formatMessage(validationMessages.discountCodeModalMax),
          "max": intl.formatMessage(validationMessages.discountCodeModalMax),
        }
      }
    },
    {
      "fieldName": "discountCode2",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "required": false,
          "matches": "^(?! )[A-Za-z0-9]{1,6}$",
          "min": 6,
          "max": 6,
        },
        "msgs": {
          "required": intl.formatMessage(validationMessages.discountCodeModalMin),
          "matches": intl.formatMessage(validationMessages.discountCodeModalMatches),
          "min": intl.formatMessage(validationMessages.discountCodeModalMax),
          "max": intl.formatMessage(validationMessages.discountCodeModalMax),
        }
      }
    },
  ]


  const notificationBannerFormValidationObj = [
    {
      "fieldName": "title",
      "validations": {
        "validators": {
          "required": true,
          "matches": "^$|^(?!\\s)(?![\\s\\S]*\\s$)[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\-']+(s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\- '.,()?!]+)*$",
          "min": 10,
          "max": 50
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.notificationBannerTitleMin),
          "max": intl.formatMessage(validationMessages.notificationBannerTitleMax),
          "matches": intl.formatMessage(validationMessages.notificationBannerTitleMatches),
          "required": intl.formatMessage(validationMessages.notificationBannerTitleRequired)
        }
      },
    },
    {
      "fieldName": "message",
      "validations": {
        // "skipValidation": true,
        "validators": {
          "matches": "^$|^(?!\\s)(?![\\s\\S]*\\s$)[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\-']+(s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\- '.,()?!]+)*$",
          "required": true,
          "min": 30,
          "max": 200
        },
        "msgs": {
          "max": intl.formatMessage(validationMessages.notificationBannerMessageMax),
          "min": intl.formatMessage(validationMessages.notificationBannerMessageMin),
          "matches": intl.formatMessage(validationMessages.notificationBannerMessageMatches),
          "required": intl.formatMessage(validationMessages.notificationBannerMessageRequired)
        }
      },
    },
  ]

  const guestProfileFormValidationObjAC = [
    {
      "fieldName": "firstName",
      "validations": {
        "validators": {
          "required": true,
          "matches": "^$|^[0-9a-zA-ZáéíñóúüàâæçèêëîïôœùûÿÁÉÍÑÓÚÜÀÂÆÇÈÊËÎÏÔŒÙÛŸ0\\-']+(s{0,}[ 0-9a-zA-ZáéíñóúüàâæçèêëîïôœùûÿÁÉÍÑÓÚÜÀÂÆÇÈÊËÎÏÔŒÙÛŸ0\\- ']+)*$",
          "min": 1,
          "max": 99
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.guestFirstNameMin),
          "max": intl.formatMessage(validationMessages.guestFirstNameMax),
          "matches": intl.formatMessage(validationMessages.guestFirstNameMatches),
          "required": intl.formatMessage(validationMessages.guestFirstNameRequired)
        }
      },
    },
    {
      "fieldName": "lastName",
      "validations": {
        "validators": {
          "required": true,
          "matches": "^$|^[0-9a-zA-ZáéíñóúüàâæçèêëîïôœùûÿÁÉÍÑÓÚÜÀÂÆÇÈÊËÎÏÔŒÙÛŸ0\\-']+(s{0,}[ 0-9a-zA-ZáéíñóúüàâæçèêëîïôœùûÿÁÉÍÑÓÚÜÀÂÆÇÈÊËÎÏÔŒÙÛŸ0\\- ']+)*$",
          "min": 1,
          "max": 99
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.guestLastNameMin),
          "max": intl.formatMessage(validationMessages.guestLastNameMax),
          "matches": intl.formatMessage(validationMessages.guestLastNameMatches),
          "required": intl.formatMessage(validationMessages.guestLastNameRequired)
        }
      },
    },
    {
      "fieldName": "middleName",
      "validations": {
        "validators": {
          "required": false,
          "matches": "^$|^[0-9a-zA-ZáéíñóúüàâæçèêëîïôœùûÿÁÉÍÑÓÚÜÀÂÆÇÈÊËÎÏÔŒÙÛŸ0\\-']+(s{0,}[ 0-9a-zA-ZáéíñóúüàâæçèêëîïôœùûÿÁÉÍÑÓÚÜÀÂÆÇÈÊËÎÏÔŒÙÛŸ0\\- ']+)*$",
          "min": 1,
          "max": 99
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.guestMiddleNameMin),
          "max": intl.formatMessage(validationMessages.guestMiddleNameMax),
          "matches": intl.formatMessage(validationMessages.guestMiddleNameMatches),
        }
      },
    },
    {
      "fieldName": "emailAddress",
      "validations": {
        "validators": {
          "required": false,
          "matches": "^(?![.])[a-zA-Z0-9.!#$%&'*+=?^_`{|}~-]+@([a-zA-Z0-9-]+\\.)+[a-zA-Z0-9-]{2,100}$",
          "min": 3,
          "max": 320
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.guestEmailMin),
          "max": intl.formatMessage(validationMessages.guestEmailMax),
          "matches": intl.formatMessage(validationMessages.guestEmailMatches),
        }
      },
    },
  ]

  const guestProfileFormValidationObj = [
    {
      "fieldName": "firstName",
      "validations": {
        "validators": {
          "required": true,
          "matches": "^$|^[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\-']+(s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\- ']+)*$",
          "min": 1,
          "max": 99
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.guestFirstNameMin),
          "max": intl.formatMessage(validationMessages.guestFirstNameMax),
          "matches": intl.formatMessage(validationMessages.guestFirstNameMatches),
          "required": intl.formatMessage(validationMessages.guestFirstNameRequired)
        }
      },
    },
    {
      "fieldName": "lastName",
      "validations": {
        "validators": {
          "required": true,
          "matches": "^$|^[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\-']+(s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\- ']+)*$",
          "min": 1,
          "max": 99
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.guestLastNameMin),
          "max": intl.formatMessage(validationMessages.guestLastNameMax),
          "matches": intl.formatMessage(validationMessages.guestLastNameMatches),
          "required": intl.formatMessage(validationMessages.guestLastNameRequired)
        }
      },
    },
    {
      "fieldName": "middleName",
      "validations": {
        "validators": {
          "required": false,
          "matches": "^$|^[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\-']+(s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\- ']+)*$",
          "min": 1,
          "max": 99
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.guestMiddleNameMin),
          "max": intl.formatMessage(validationMessages.guestMiddleNameMax),
          "matches": intl.formatMessage(validationMessages.guestMiddleNameMatches),
        }
      },
    },
    {
      "fieldName": "suffix",
      "validations": {
        "validators": {
          "required": false,
          "matches": "^[A-Za-z]+$",
          "max": 5,
        },
        "msgs": {
          "matches": intl.formatMessage(validationMessages.guestSuffixMatches),
          "max": intl.formatMessage(validationMessages.guestSuffixMax),
        }
      }
    },
    {
      "fieldName": "dateOfBirth",
      "validations": {
        "validators": {
          "required": true,
          "matches": "^(0[1-9]|1[0-2])\\/(0[1-9]|[12][0-9]|3[01])\\/\\d{4}$",
          "min": 10,
          "max": 10
        },
        "msgs": {
          "required": intl.formatMessage(validationMessages.guestDobRequired),
          "min": intl.formatMessage(validationMessages.guestDobMinMaxMatches),
          "max": intl.formatMessage(validationMessages.guestDobMinMaxMatches),
          "matches": intl.formatMessage(validationMessages.guestDobMinMaxMatches),
        }
      },
    },
    {
      "fieldName": "gender",
      "validations": {
        "validators": {
          "required": true,
        },
        "msgs": {
          "required": intl.formatMessage(validationMessages.guestGenderRequired),
        }
      },
    },
    {
      "fieldName": "preferredName",
      "validations": {
        "validators": {
          "required": false,
          "matches": "^$|^[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\-']+(s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\- ']+)*$",
          "min": 1,
          "max": 99
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.guestPreferredNameMin),
          "max": intl.formatMessage(validationMessages.guestPreferredNameMax),
          "matches": intl.formatMessage(validationMessages.guestPreferredNameMatches),
        }
      },
    },
    {
      "fieldName": "phoneNumber",
      "validations": {
        "validators": {
          "required": false,
        },
        "msgs": {
          "required": intl.formatMessage(validationMessages.guestPhoneRequired),
        }
      },
    },
    {
      "fieldName": "knownTravelerNumber",
      "validations": {
        "validators": {
          "required": false,
          "matches": "^[a-zA-Z0-9]+$",
          "min": 8,
          "max": 25
        },
        "msgs": {
          "matches": intl.formatMessage(validationMessages.guestKtnMatches),
          "min": intl.formatMessage(validationMessages.guestKtnLength),
          "max": intl.formatMessage(validationMessages.guestKtnLength)
        }
      }
    },
    {
      "fieldName": "redressNumber",
      "validations": {
        "validators": {
          "required": false,
          "matches": "^\\d+$",
          "min": 7,
          "max": 13
        },
        "msgs": {
          "matches": intl.formatMessage(validationMessages.guestRedressMatches),
          "min": intl.formatMessage(validationMessages.guestRedressMin),
          "max": intl.formatMessage(validationMessages.guestRedressMax)
        }
      }
    },
    {
      "fieldName": "emailAddress",
      "validations": {
        "validators": {
          "required": false,
          "matches": "^(?![.])[a-zA-Z0-9.!#$%&'*+=?^_`{|}~-]+@([a-zA-Z0-9-]+\\.)+[a-zA-Z0-9-]{2,100}$",
          "min": 3,
          "max": 320
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.guestEmailMin),
          "max": intl.formatMessage(validationMessages.guestEmailMax),
          "matches": intl.formatMessage(validationMessages.guestEmailMatches),
        }
      },
    },
    {
      "fieldName": "membershipNumber",
      "validations": {
        "validators": {
          "required": false,
          "matches": "^[a-zA-Z0-9]+$",
          "min": 1,
          "max": 255
        },
        "msgs": {
          "matches": intl.formatMessage(validationMessages.guestMembershipNumberMatches),
          "min": intl.formatMessage(validationMessages.guestMembershipNumberMin),
          "max": intl.formatMessage(validationMessages.guestMembershipNumberMax),
        }
      }
    },
    {
      "fieldName": "expirationDate",
      "validations": {
        "validators": {
          "required": false,
          "matches": "^(0[1-9]|1[0-2])\\/(0[1-9]|[12][0-9]|3[01])\\/\\d{4}$",
          "min": 10,
          "max": 10
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.guestExpirationDateMinMaxMatches),
          "max": intl.formatMessage(validationMessages.guestExpirationDateMinMaxMatches),
          "matches": intl.formatMessage(validationMessages.guestExpirationDateMinMaxMatches),
        }
      },
    },
  ]

  const editAllotmentMockedValidationObj = [
    {
      "fieldName": "lepBalance",
      "validations": {
        "validators": {
          "required": true,
          "matches": "^[0-9]{1,3}(,[0-9]{3})*(\s{0,}[0-9]+)*$",
          "min": 1,
          //custom min and max set in component
          "max": null
        },
        "msgs": {
          "min": intl.formatMessage(validationMessages.pointValueMin),
          "max": "",
          "matches": intl.formatMessage(validationMessages.pointValueMatches),
          "required": intl.formatMessage(validationMessages.pointValueRequired)
        }
      },
    }
  ]

  return {
    travelerDetailsMockedApiValidationObj,
    editTeamMockedValidationObj,
    createTeamMockedValidationObj,
    companyInfoMockedApiValidationObj,
    invitePeopleMockedApiValidationObj,
    inviteAuthorisedMockedValidationObj,
    addDomainMockedValidationObj,
    tranferMilesMockedValidationObj,
    downloadReceiptsMockedValidationObj,
    createEscalationFormValidationObj,
    inviteGuestMockedApiValidationObj,
    maintenanceFormValidationObj,
    updateEmailMockedApiValidationObj,
    ucLoginMockedApiValidationObj,
    ucJoinUnitedMockedApiValidationObj,
    specificPolicyMockedApiValidationObj,
    ucJoinDeltaMockedApiValidationObj,
    createPolicyMockedValidationObj,
    editPolicyMockedValidationObj,
    editHotelPolicyMockedValidationObj,
    addCitiEmailMockedApiValidationObj,
    updateCitiEmailMockedApiValidationObj,
    editLepTierMockedValidationObj,
    updateCitiPCANMockedApiValidationObj,
    editCitiPCANMockedApiValidationObj,
    fareAccessValidationObj,
    addBookingFieldMockedValidationObj,
    notificationBannerFormValidationObj,
    overrideDiscountTierApiValidationObj,
    companyInfoMockedApiValidationObjAS,
    companyInfoMockedApiValidationObjAM,
    editLabelMockedValidationObj,
    guestProfileFormValidationObj,
    guestProfileFormValidationObjAC,
    editAllotmentMockedValidationObj,
    companyInfoMockedApiValidationObjChoice,
    companyInfoMockedApiValidationObjAC,
  };
}

export const useValidationEditProfileService = (props = {}) => {
  const {fieldName, fieldLabel} = props;
  const intl = useIntl();

  const editProfileValidationObj = useMemo(() => ([{
    "fieldName": fieldName,
    "validations": {
      "validators": {
        "required": true,
        "matches": "^$|^[a-zA-Z\\u00C0-\\u024F]+([' -][a-zA-Z\\u00C0-\\u024F]+)*$",
        "min": 2,
        "max": 50
      },
      "msgs": {
        "min": intl.formatMessage(validationMessages.editInputModalFieldMin, { fieldLabel, min: 2 }),
        "max": intl.formatMessage(validationMessages.editInputModalFieldMax, { fieldLabel, max: 50 }),
        "matches": intl.formatMessage(validationMessages.editInputModalFieldMatches, { fieldLabel }),
        "required": intl.formatMessage(validationMessages.editInputModalFieldRequired, { fieldLabel })
      }
    },
  }]), [fieldName]);

  return {
    editProfileValidationObj
  };
}

// mocked validations scheme that are not used
export const simpleMockedApiValidationObj = [
  {
    "fieldName": "firstname",
    "validations": {
      //"skipValidation": true,
      //"validatedWith": ["lastname"],
      "validators": {
        "required": true,
        "matches": "^$|^[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0-9]+(s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0-9.!#@$&' ,;:*()+\\-_`|~\"^]+)*$",
        "min": 2,
        "max": 5
      },
      "msgs": {
        "min": "{0} too short",
        "max": "{0} too long",
        "matches": "{0} can contain letter,numbers,space or special characters and cannot start with space",
        "required": "{0} is required"
      }
    }
  },
  {
    "fieldName": "lastname",
    "validations": {
      //"skipValidation": true,
      //"validatedWith": ["firstname"],
      "validators": {
        "required": true,
        "matches": "^$|^[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0-9]+(s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0-9.!#@$&' ,;:*()+\\-_`|~\"^]+)*$",
        "min": 2,
        "max": 5
      },
      "msgs": {
        "min": "{0} too short",
        "max": "{0} too long",
        "matches": "{0} can contain letter,numbers,space or special characters and cannot start with space",
        "required": "{0} is required"
      }
    }
  },
]

export const mockedApiValidationObj = [
  {
    "fieldName": "teamName",
    "validations": {
      "skipValidation": false,
      "validators": {
        "fieldRequired": true,
        "regex": "^$|^[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0-9]+(s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0-9.!#@$&' ,;:*()+\\-_`|~\"^]+)*$",
        "minLength": 2,
        "maxLength": 40
      },
      "msgs": {
        "minLength": "{0} too short",
        "maxLength": "{0} too long",
        "regex": "{0} can contain letter,numbers,space or special characters and cannot start with space",
        "fieldRequired": "{0} is required"
      }
    }
  },
  {
    "fieldName": "traveler.middleName",
    "validations": {
      "skipValidation": true,
      "validators": {
        "fieldRequired": false,
        "regex": "^$|^[a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\-']+(s{0,}[a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\- ']+)*$",
        "minLength": 2,
        "maxLength": 40
      },
      "msgs": {
        "minLength": "{0} too short",
        "maxLength": "{0} too long",
        "regex": "<ul><li>This field accepts only the following characters: a-z, hyphen (-), apostrophe (') and space but cannot start with space</li> <li>First name and last name must have at least 2 characters, maximum 40 characters</li></ul>",
        "fieldRequired": "{0} is required"
      }
    }
  },
  {
    "fieldName": "traveler.lastName",
    "validations": {
      "skipValidation": true,
      "validators": {
        "fieldRequired": true,
        "regex": "^$|^[a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\-']+(s{0,}[a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\- ']+)*$",
        "minLength": 2,
        "maxLength": 40
      },
      "msgs": {
        "minLength": "{0} too short",
        "maxLength": "{0} too long",
        "regex": "<ul><li>This field accepts only the following characters: a-z, hyphen (-), apostrophe (') and space but cannot start with space</li> <li>First name and last name must have at least 2 characters, maximum 40 characters</li></ul>",
        "fieldRequired": "{0} is required"
      }
    }
  },
  {
    "fieldName": "teamEntityId",
    "validations": {
      "skipValidation": false,
      "validators": {
        "fieldRequired": true,
        "regex": "^[0-9]*$",
        "minLength": 1,
        "maxLength": 20
      },
      "msgs": {
        "minLength": "{0} too short",
        "maxLength": "{0} too long",
        "regex": "{0} can contain only numbers",
        "fieldRequired": "{0} is required"
      }
    }
  },
  {
    "fieldName": "addressType",
    "validations": {
      "skipValidation": false,
      "validators": {
        "fieldRequired": true,
        "regex": null,
        "minLength": 2,
        "maxLength": 20
      },
      "msgs": {
        "minLength": "{0} too short",
        "maxLength": "{0} too long",
        "regex": null,
        "fieldRequired": "{0} is required"
      }
    }
  },
  {
    "fieldName": "traveler.aeroplanNumber",
    "validations": {
      "skipValidation": true,
      "validators": {
        "fieldRequired": false,
        "regex": "^$|^[a-zA-Z0-9]+([a-zA-Z0-9s ]{1}[a-zA-Z0-9 ]+)*$",
        "minLength": 2,
        "maxLength": 40
      },
      "msgs": {
        "minLength": "{0} too short",
        "maxLength": "{0} too long",
        "regex": "{0} can only contain letters or numbers and cannot begin nor end with a 'space'.",
        "fieldRequired": "{0} is required"
      }
    }
  },
  {
    "fieldName": "teamAddress.postalCode",
    "validations": {
      "skipValidation": true,
      "validators": {
        "fieldRequired": true,
        "regex": "^$|^[a-zA-Z0-9-.]+(s{0,}[a-zA-Z0-9-. ]+)*$",
        "minLength": 2,
        "maxLength": 10
      },
      "msgs": {
        "minLength": "{0} too short",
        "maxLength": "{0} too long",
        "regex": "ALTERNATE ADDRESS",
        "fieldRequired": "{0} is required"
      }
    }
  },
  {
    "fieldName": "traveler.locale",
    "validations": {
      "skipValidation": true,
      "validators": {
        "fieldRequired": true,
        "regex": null,
        "minLength": 1,
        "maxLength": 40
      },
      "msgs": {
        "minLength": "{0} too short",
        "maxLength": "{0} too long",
        "regex": null,
        "fieldRequired": "{0} is required"
      }
    }
  },
  {
    "fieldName": "traveler.dateOfBirth",
    "validations": {
      "skipValidation": false,
      "validators": {
        "fieldRequired": false,
        "regex": "^(0[1-9]|[1-2][0-9]|3[0-1])\\/(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\\/?([0-9]{4})$",
        "minLength": 7,
        "maxLength": 11
      },
      "msgs": {
        "minLength": "{0} too short",
        "maxLength": "{0} too long",
        "regex": null,
        "fieldRequired": "{0} is required"
      }
    }
  },
  {
    "fieldName": "teamAddress.city",
    "validations": {
      "skipValidation": true,
      "validators": {
        "fieldRequired": true,
        "regex": "^$|^[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0-9]+(s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0-9.!#@$&' ,;:*()+\\-_`|~\"^]+)*$",
        "minLength": 2,
        "maxLength": 40
      },
      "msgs": {
        "minLength": "{0} too short",
        "maxLength": "{0} too long",
        "regex": "{0} can contain letter,numbers,space or special characters and cannot start with space",
        "fieldRequired": "{0} is required"
      }
    }
  },
  {
    "fieldName": "traveler.firstName",
    "validations": {
      "skipValidation": true,
      "validators": {
        "fieldRequired": true,
        "regex": "^$|^[a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\-']+(s{0,}[a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0\\- ']+)*$",
        "minLength": 2,
        "maxLength": 40
      },
      "msgs": {
        "minLength": "{0} too short",
        "maxLength": "{0} too long",
        "regex": "<ul><li>This field accepts only the following characters: a-z, hyphen (-), apostrophe (') and space but cannot start with space</li> <li>First name and last name must have at least 2 characters, maximum 40 characters</li></ul>",
        "fieldRequired": "{0} is required"
      }
    }
  },
  {
    "fieldName": "teamAddress.streetAddress",
    "validations": {
      "skipValidation": true,
      "validators": {
        "fieldRequired": true,
        "regex": "^$|^[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0-9]+(s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0-9.!#@$&' ,;:*()+\\-_`|~\"^]+)*$",
        "minLength": 2,
        "maxLength": 40
      },
      "msgs": {
        "minLength": "{0} too short",
        "maxLength": "{0} too long",
        "regex": "{0} can contain letter,numbers,space or special characters and cannot start with space",
        "fieldRequired": "{0} is required"
      }
    }
  },
  {
    "fieldName": "phone.phoneNumber",
    "validations": {
      "skipValidation": false,
      "validators": {
        "fieldRequired": false,
        "regex": "^[0-9]*$",
        "minLength": 1,
        "maxLength": 255
      },
      "msgs": {
        "minLength": "{0} too short",
        "maxLength": "{0} too long",
        "regex": "{0} can contain only numbers",
        "fieldRequired": "{0} is required"
      }
    }
  },
  {
    "fieldName": "phone.country",
    "validations": {
      "skipValidation": false,
      "validators": {
        "fieldRequired": false,
        "regex": null,
        "minLength": 0,
        "maxLength": 0
      },
      "msgs": {
        "minLength": "{0} too short",
        "maxLength": "{0} too long",
        "regex": null,
        "fieldRequired": "{0} is required"
      }
    }
  },
  {
    "fieldName": "phone.extension",
    "validations": {
      "skipValidation": false,
      "validators": {
        "fieldRequired": false,
        "regex": "^[0-9]*$",
        "minLength": 1,
        "maxLength": 255
      },
      "msgs": {
        "minLength": "{0} too short",
        "maxLength": "{0} too long",
        "regex": "{0} can contain only numbers",
        "fieldRequired": "{0} is required"
      }
    }
  },
  {
    "fieldName": "traveler.emailAddress",
    "validations": {
      "skipValidation": true,
      "validators": {
        "fieldRequired": true,
        "regex": "^[a-zA-Z0-9][a-zA-Z0-9.!#$%&'*+=?^_`{|}~-]+@([a-zA-Z0-9-]+\\.)+[a-zA-Z0-9-]{2,100}$",
        "minLength": 2,
        "maxLength": 40
      },
      "msgs": {
        "minLength": "{0} too short",
        "maxLength": "{0} too long",
        "regex": "{0} must be a valid email address",
        "fieldRequired": "{0} is required"
      }
    }
  },
  {
    "fieldName": "teamAddress.streetAddress2",
    "validations": {
      "skipValidation": true,
      "validators": {
        "fieldRequired": false,
        "regex": "^$|^[0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0-9]+(s{0,}[ 0-9a-zA-ZáéíñóúüÁÉÍÑÓÚÜ0-9.!#@$&' ,;:*()+\\-_`|~\"^]+)*$",
        "minLength": 2,
        "maxLength": 40
      },
      "msgs": {
        "minLength": "{0} too short",
        "maxLength": "{0} too long",
        "regex": "{0} can contain letter,numbers,space or special characters and cannot start with space",
        "fieldRequired": "{0} is required"
      }
    }
  },
  {
    "fieldName": "phone.phoneType",
    "validations": {
      "skipValidation": false,
      "validators": {
        "fieldRequired": false,
        "regex": null,
        "minLength": 0,
        "maxLength": 0
      },
      "msgs": {
        "minLength": "{0} too short",
        "maxLength": "{0} too long",
        "regex": null,
        "fieldRequired": "{0} is required"
      }
    }
  },
  {
    "fieldName": "teamAddress.state",
    "validations": {
      "skipValidation": true,
      "validators": {
        "fieldRequired": true,
        "regex": null,
        "minLength": 2,
        "maxLength": 40
      },
      "msgs": {
        "minLength": "{0} too short",
        "maxLength": "{0} too long",
        "regex": null,
        "fieldRequired": "{0} is required"
      }
    }
  },
  {
    "fieldName": "teamAddress.country",
    "validations": {
      "skipValidation": true,
      "validators": {
        "fieldRequired": true,
        "regex": null,
        "minLength": 2,
        "maxLength": 40
      },
      "msgs": {
        "minLength": "{0} too short",
        "maxLength": "{0} too long",
        "regex": null,
        "fieldRequired": "{0} is required"
      }
    }
  },
  {
    "fieldName": "companyEntityId",
    "validations": {
      "skipValidation": false,
      "validators": {
        "fieldRequired": true,
        "regex": "^[0-9]*$",
        "minLength": 1,
        "maxLength": 20
      },
      "msgs": {
        "minLength": "{0} too short",
        "maxLength": "{0} too long",
        "regex": "{0} can contain only numbers",
        "fieldRequired": "{0} is required"
      }
    }
  },
  {
    "fieldName": "traveler.confirmEmailAddress",
    "validations": {
      "skipValidation": true,
      "validators": {
        "fieldRequired": true,
        "regex": "^[a-zA-Z0-9][a-zA-Z0-9.!#$%&'*+=?^_`{|}~-]+@([a-zA-Z0-9-]+\\.)+[a-zA-Z0-9-]{2,100}$",
        "minLength": 2,
        "maxLength": 40
      },
      "msgs": {
        "minLength": "{0} too short",
        "maxLength": "{0} too long",
        "regex": "{0} must be a valid email address",
        "fieldRequired": "{0} is required"
      }
    }
  }
]

